import {
  BillingSource,
  CompanyFlag,
  CompanyFlagUpdatePayload,
  FlagSummary,
  GlobalFlagUpdatePayload,
  GlobalOnChoice,
  LocationFlagChange,
  LocationFlagUpdatePayload,
} from '../models/feature-flag.model';

/** Changes can take up to 30 seconds to reach running services. */
export const FEATURE_FLAG_PROPAGATION_NOTE = 'Changes can take up to 30 seconds to apply.';

/** A flag description holds one line per paragraph (the registry joins its lines with newlines). */
export function descriptionLines(description: string): string[] {
  return description
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
}

export function onOffLabel(value: boolean): string {
  return value ? 'On' : 'Off';
}

export function buildCompanyPayload(enabled: boolean): CompanyFlagUpdatePayload {
  return { enabled };
}

/** A location switch can only turn the flag off; switching it back on follows the company again. */
export function buildLocationPayload(enabled: boolean): LocationFlagUpdatePayload {
  return { enabled: enabled ? null : false };
}

/** Locations can only be turned off, so they are locked when global or company is off. */
export function areLocationControlsDisabled(flag: CompanyFlag): boolean {
  return !flag.global_enabled || !flag.company_enabled;
}

export function locationControlsNote(flag: CompanyFlag): string {
  if (!flag.global_enabled) {
    return 'Off globally, so every location is off. Turn it on from the Feature Flags page.';
  }
  if (!flag.company_enabled) {
    return 'Off for this company, so every location is off.';
  }
  return '';
}

export function buildGlobalPayload(
  enabled: boolean,
  choice: GlobalOnChoice = 'all'
): GlobalFlagUpdatePayload {
  if (!enabled) {
    return { enabled: false };
  }
  return { enabled: true, companyDefault: choice === 'all' ? 'on' : 'off' };
}

export function globalConfirmTitle(flag: FlagSummary, turnOn: boolean): string {
  return `${turnOn ? 'Turn on' : 'Turn off'} ${flag.key}?`;
}

export function globalOffMessage(): string {
  return 'This turns the feature off everywhere. Company and location settings are kept.';
}

/** Picker labels; `{{count}}` is filled in by the dropdown. */
export const LOCATION_PICKER_LABELS = Object.freeze({
  all: 'All locations',
  none: 'No locations',
  many: '{{count}} locations',
});

export interface FlagLocationOption {
  id: string;
  name: string;
}

/** One switch of the company Features table: one flag's company setting. */
export interface FlagSwitch {
  key: string;
  /** Off while the flag is off globally, whatever the company setting (kept for when it is back on). */
  enabled: boolean;
  label: string;
  /** Cannot be moved while the flag is off globally. */
  locked: boolean;
  ariaLabel: string;
  flag: CompanyFlag;
}

/** Everything one row of the company Features table binds to: one feature. */
export interface CompanyFlagRow {
  key: string;
  title: string;
  descriptionLines: string[];
  /** A feature split by who bills the membership: a switch per source, null where it has none. */
  way: FlagSwitch | null;
  imported: FlagSwitch | null;
  /** A feature not split by who bills the membership: one switch for every membership. */
  all: FlagSwitch | null;
  /** Behind the (i): where the setting comes from, or why the company's locations are locked. */
  tip: string;
  /** Shown under the description, and only there: why everything is locked while off globally. */
  globalOffNote: string;
  locationOptions: FlagLocationOption[];
  /** Locations the feature is not turned off at. */
  selectedLocationIds: string[];
  locationsLocked: boolean;
  /** The feature's own flag, which holds the locations every one of its switches follows. */
  flag: CompanyFlag;
}

/** "update_payment_methods" → "Update payment methods". */
export function flagTitle(key: string): string {
  const words = key.split('_').filter(Boolean).join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

const BILLING_LABELS: Readonly<Record<BillingSource, string>> = Object.freeze({
  way: 'Way-billed',
  imported: 'Imported',
});

/** Turning a switch flips that one flag's company setting. */
export function buildSwitchPayload(toggle: FlagSwitch): CompanyFlagUpdatePayload {
  return buildCompanyPayload(!toggle.flag.company_enabled);
}

/** Where the settings come from, naming the source when only one of them follows the default. */
function settingTip(members: CompanyFlag[]): string {
  const following = members.filter(flag => flag.company_override === null);
  if (following.length === 0) {
    return '';
  }
  const fallback = `the default (${members[0].company_default})`;
  if (following.length === members.length) {
    return `Following ${fallback}.`;
  }
  const names = following.map(flag => (flag.billing ? BILLING_LABELS[flag.billing] : flag.key));
  return `${names.join(' and ')} follows ${fallback}.`;
}

export function selectedLocationIds(flag: CompanyFlag): string[] {
  return flag.locations.filter(location => !location.off).map(location => location.id);
}

/** The location switches to flip so the flag is on at exactly `selectedIds`. */
export function locationChanges(flag: CompanyFlag, selectedIds: string[]): LocationFlagChange[] {
  const wanted = new Set(selectedIds);
  return flag.locations
    .filter(location => wanted.has(location.id) === location.off)
    .map(location => ({
      locationId: location.id,
      payload: buildLocationPayload(wanted.has(location.id)),
    }));
}

function flagSwitch(flag: CompanyFlag, title: string, memberships: string): FlagSwitch {
  // Off globally is off for every company, whatever its own setting.
  const enabled = flag.global_enabled && flag.company_enabled;
  return {
    key: flag.key,
    enabled,
    label: onOffLabel(enabled),
    locked: !flag.global_enabled,
    ariaLabel: `${title} for ${memberships}`,
    flag,
  };
}

/** A feature's flag with its sub-flags, in the order the API listed them. */
function groupByFeature(flags: CompanyFlag[]): CompanyFlag[][] {
  const features = new Map<string, CompanyFlag[]>();
  flags.forEach(flag => {
    const key = flag.parent || flag.key;
    features.set(key, [...(features.get(key) ?? []), flag]);
  });
  return [...features.values()];
}

/** Behind the (i). Off globally is left out: the note under the description already says it. */
function rowTip(feature: CompanyFlag, members: CompanyFlag[], locationsLocked: boolean): string {
  if (!feature.global_enabled) {
    return '';
  }
  return locationsLocked ? locationControlsNote(feature) : settingTip(members);
}

function buildFeatureRow(members: CompanyFlag[]): CompanyFlagRow {
  const feature = members.find(flag => !flag.parent) ?? members[0];
  const key = feature.parent || feature.key;
  const title = flagTitle(key);
  const way = members.find(flag => flag.billing === 'way');
  const imported = members.find(flag => flag.billing === 'imported');
  const split = Boolean(way || imported);
  // A location is off for every switch of the feature: it can only be set while one of them is on.
  const locationsLocked = !feature.global_enabled || members.every(flag => !flag.company_enabled);
  return {
    key,
    title,
    descriptionLines: descriptionLines(feature.description),
    way: way ? flagSwitch(way, title, 'Way-billed memberships') : null,
    imported: imported ? flagSwitch(imported, title, 'imported memberships') : null,
    all: split ? null : flagSwitch(feature, title, 'this company'),
    tip: rowTip(feature, members, locationsLocked),
    globalOffNote: feature.global_enabled ? '' : locationControlsNote(feature),
    locationOptions: feature.locations.map(location => ({
      id: location.id,
      name: location.branch || location.id,
    })),
    selectedLocationIds: selectedLocationIds(feature),
    locationsLocked,
    flag: feature,
  };
}

export function buildFlagRows(flags: CompanyFlag[]): CompanyFlagRow[] {
  return groupByFeature(flags).map(buildFeatureRow);
}
